"use client";
import AcademicProductCard from "@/app/views/AcademicProductCard/components/AcademicProductCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { CartContext } from "@/context/CartContext";
import CustomBreadcrumbs from "@/custom/CustomBreadcrumbs";
import { useToast } from "@/hooks/use-toast";
import { useAcademicSetStore } from "@/store/AcademicSet";
import { formatCurrency } from "@/utils/formatCurrency";
import { LibraryBig, ShoppingCart } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useContext, useEffect } from "react";
import Filters from "./Filters";

const Products = ({
  academicProducts,
  stationaryProducts,
  totalAcademic,
  totalStationary,
  schoolClasses,
  range,
}) => {
  const searchParams = useSearchParams();
  const school = searchParams.get("school");
  const className = searchParams.get("class");
  const { setAddToCart } = useContext(CartContext);
  const { success, warning } = useToast();

  const {
    setSchoolSet,
    removeSchoolSet,
    schoolSet,
    isAllProductSelect,
    setAllProductSelect,
    totalPrice,
    totalSaveAmount,
  } = useAcademicSetStore();

  useEffect(() => {
    setSchoolSet([...academicProducts, ...stationaryProducts]);
    setAllProductSelect(true);
  }, [academicProducts, stationaryProducts]);

  return (
    <div className="my-10 container mx-auto px-4">
      <div className="space-y-10">
        <CustomBreadcrumbs text={school} />
        <div className="flex flex-col lg:flex-row items-start gap-4">
          <Filters schoolClasses={schoolClasses} />
          <div className="space-y-3 w-full">
            <Card className="flex flex-col gap-4 w-full border-brand-light shadow-brand-sm rounded-2xl overflow-hidden">
              <CardHeader className="sticky top-16 md:top-20 z-50 bg-white border-t border-b border-brand-light">
                <div className="flex flex-col sm:flex-row justify-between items-center w-full">
                  <div className="flex flex-wrap h-fit items-center gap-2 text-sm">
                    <p className="text-base text-brand-navy font-semibold">
                      {range}
                    </p>
                    <Badge
                      variant="outline"
                      className="capitalize border-brand-sky text-brand-deep bg-brand-light/40"
                    >
                      search: {school}
                    </Badge>

                    {className && (
                      <Badge
                        variant="outline"
                        className="capitalize border-brand-sky text-brand-deep bg-brand-light/40"
                      >
                        Class : {className}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 items-center">
                    <div className="text-center text-sm font-medium">
                      {totalPrice > 0 && (
                        <Badge
                          variant="outline"
                          className="capitalize border-brand-sky text-brand-deep bg-brand-light/40"
                        >
                          Total: {formatCurrency(totalPrice)}
                        </Badge>
                      )}
                      {totalSaveAmount > 0 && (
                        <Badge
                          variant="outline"
                          className="capitalize border-brand-sky text-brand-deep bg-brand-light/40"
                        >
                          save {formatCurrency(totalSaveAmount)}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 select-none">
                      <Checkbox
                        id="selected"
                        checked={isAllProductSelect}
                        onClick={(e) => {
                          setAllProductSelect(!isAllProductSelect);
                          if (!isAllProductSelect === true) {
                            setSchoolSet([
                              ...academicProducts,
                              ...stationaryProducts,
                            ]);
                          } else {
                            removeSchoolSet();
                          }
                        }}
                        className="border-brand-sky data-[state=checked]:bg-brand-sky data-[state=checked]:border-brand-sky data-[state=checked]:text-white"
                      />
                      <label
                        htmlFor="selected"
                        className="text-sm font-medium cursor-pointer text-brand-navy"
                      >
                        {isAllProductSelect ? "Unselect All" : "Select All"}
                      </label>
                    </div>
                    <div className="flex flex-col items-center">
                      <Button
                        className="gradient-primary hover:opacity-95 h-full text-sm text-white rounded-xl flex items-center gap-2 font-semibold shadow-brand hover:shadow-brand-lg transition-all"
                        onClick={() => {
                          if (schoolSet?.length === 0) {
                            warning("Select at least one item to add to your cart.");
                          } else {
                            success("Academic set was added to the shopping cart.");
                            setAddToCart(schoolSet);
                            window.dataLayer = window.dataLayer || [];
                            window.dataLayer.push({ ecommerce: null });
                            window.dataLayer.push({
                              event: "add_to_cart",
                              ecommerce: {
                                currency: "BDT",
                                value: totalPrice,
                                items: schoolSet,
                              },
                            });
                          }
                        }}
                      >
                        <ShoppingCart size={16} /> Add All to cart
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <div>
                  <div className="flex gap-3">
                    <h3 className="font-semibold text-lg pb-5 text-brand-navy">
                      Academic Books
                    </h3>
                    <span className="text-muted-foreground">
                      ( Total : {totalAcademic} )
                    </span>
                  </div>
                  {academicProducts?.length === 0 ? (
                    <Card className="border-brand-light shadow-brand-sm rounded-2xl">
                      <CardHeader>
                        <CardTitle className="text-xl font-medium text-brand-navy">
                          <LibraryBig strokeWidth={1} size={45} className="text-brand-sky" />
                          <p>No Products Found</p>
                        </CardTitle>
                        <CardDescription className="text-muted-foreground">
                          We couldn&apos;t find any products matching your
                          criteria. Please adjust your search or reset filters.
                          For assistance, contact our support. Thank you.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                      {academicProducts?.map((book, i) => (
                        <div
                          className="w-full relative overflow-hidden"
                          key={i}
                        >
                          <AcademicProductCard product={book} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="flex flex-col gap-4 w-full border-brand-light shadow-brand-sm rounded-2xl overflow-hidden">
              <CardHeader className="pb-2 bg-brand-light/40">
                <div className="flex gap-3">
                  <h3 className="font-semibold text-lg text-brand-navy">Stationary</h3>
                  <span className="text-muted-foreground">
                    ( Total : {totalStationary} )
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-6 pt-4">
                {stationaryProducts?.length === 0 ? (
                  <Card className="border-brand-light shadow-brand-sm rounded-2xl">
                    <CardHeader>
                      <CardTitle className="text-xl font-medium text-brand-navy">
                        <LibraryBig strokeWidth={1} size={45} className="text-brand-sky" />
                        <p>No Products Found</p>
                      </CardTitle>
                      <CardDescription className="text-muted-foreground">
                        We couldn&apos;t find any products matching your
                        criteria. Please adjust your search or reset filters.
                        For assistance, contact our support. Thank you.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                    {stationaryProducts?.map((book, i) => (
                      <div key={i} className="w-full relative overflow-hidden">
                        <AcademicProductCard product={book} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;